const AUTH_CODE_TO_KEY = {
  ID_PASSWORD_REQUIRED: ['auth.login.error.inputRequired', 'Please enter your ID and password.'],
  INVALID_LOGIN_INPUT: ['auth.login.error.invalidInput', 'The login input is invalid.'],
  ALREADY_LOGGED_IN: ['auth.login.error.alreadyLoggedIn', 'This account is already signed in.'],
  ACCOUNT_LOCKED: ['auth.login.error.accountLocked', 'This account is restricted by an administrator.'],
  LOGIN_SESSION_CHECK_FAILED: ['auth.login.error.sessionCheckFailed', 'Could not verify login status. Please try again shortly.'],
  LOGIN_IN_PROGRESS: ['auth.login.error.inProgress', 'A login is already being processed. Please try again shortly.'],
  SESSION_STORE_UNAVAILABLE: ['auth.login.error.sessionCheckFailed', 'Could not verify login status. Please try again shortly.'],
  SERVER_ERROR: ['auth.login.error.server', 'A server error occurred.'],
};

// Legacy backends may still return Korean sentence text instead of a stable code.
// These strings are INPUT MATCHERS ONLY and are never returned to the UI.
const MESSAGE_TO_CODE = new Map([
  ['아이디와 비밀번호를 입력해 주세요.', 'ID_PASSWORD_REQUIRED'],
  ['아이디 또는 비밀번호가 올바르지 않습니다.', 'INVALID_CREDENTIALS'],
  ['이미 접속 중입니다.', 'ALREADY_LOGGED_IN'],
  ['관리자에 의해 계정 이용이 제한되었습니다.', 'ACCOUNT_LOCKED'],
  ['로그인 상태 확인 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.', 'LOGIN_SESSION_CHECK_FAILED'],
  ['로그인 처리 중입니다. 잠시 후 다시 시도해 주세요.', 'LOGIN_IN_PROGRESS'],
  ['서버 오류가 발생했습니다.', 'SERVER_ERROR'],
]);

function responseData(source = {}) {
  return source?.response?.data || source?.payload || source?.data || source || {};
}

function translateKey(t, key, vars = null, fallback = '') {
  const k = String(key || '').trim();
  if (!k || typeof t !== 'function') return String(fallback || '');
  try {
    const value = t(k, vars, fallback);
    const text = value == null ? '' : String(value).trim();
    return text && text !== k ? text : String(fallback || '');
  } catch (_) {
    return String(fallback || '');
  }
}

export function getResponseCode(source = {}) {
  const data = responseData(source);
  return String(
    data?.code ||
    data?.error_code ||
    data?.errorCode ||
    data?.error ||
    source?.code ||
    ''
  ).trim().toUpperCase();
}

/**
 * Resolve API/socket errors without ever echoing a backend sentence directly.
 *
 * The server may return `message_key`/`messageKey`; those keys are safe to translate.
 * A stable code can be mapped by each caller via codeMap. Unknown server text is ignored
 * and the caller's localized fallback is shown instead. This prevents Korean (or any
 * other backend language) from leaking into a different UI language.
 */
export function resolveLocalizedResponseMessage(
  t,
  source = {},
  {
    vars = null,
    codeMap = null,
    fallbackKey = 'common.failed',
    fallback = 'The request could not be completed.',
  } = {},
) {
  const data = responseData(source);
  const messageKey = String(data?.message_key || data?.messageKey || '').trim();
  if (messageKey) {
    const translated = translateKey(t, messageKey, data?.message_vars || data?.messageVars || vars, '');
    if (translated) return translated;
  }

  const code = getResponseCode(source);
  const mapped = code && codeMap && codeMap[code];
  if (mapped) {
    if (Array.isArray(mapped)) {
      const [key, mappedFallback, mappedVars] = mapped;
      const translated = translateKey(t, key, mappedVars ?? vars, mappedFallback || fallback);
      if (translated) return translated;
    } else if (typeof mapped === 'string') {
      const translated = translateKey(t, mapped, vars, fallback);
      if (translated) return translated;
    } else if (mapped && typeof mapped === 'object') {
      const translated = translateKey(t, mapped.key, mapped.vars ?? vars, mapped.fallback || fallback);
      if (translated) return translated;
    }
  }

  return translateKey(t, fallbackKey, vars, fallback) || String(fallback || '');
}

export function resolveAuthErrorMessage(t, source = {}, fallback = '') {
  const data = responseData(source);
  const messageKey = String(data?.message_key || data?.messageKey || '').trim();
  if (messageKey) {
    const translated = translateKey(t, messageKey, data?.message_vars || data?.messageVars || null, '');
    if (translated) return translated;
  }

  let code = String(data?.error || data?.code || '').trim().toUpperCase();
  const rawMessage = String(data?.message || source?.message || '').trim();
  if (!code && rawMessage) code = MESSAGE_TO_CODE.get(rawMessage) || '';
  if (code === 'INVALID_CREDENTIALS' || (!code && rawMessage === '아이디 또는 비밀번호가 올바르지 않습니다.')) {
    return t('auth.login.error.invalidCredentials', null, 'The ID or password is incorrect.');
  }
  const mapped = AUTH_CODE_TO_KEY[code];
  if (mapped) return t(mapped[0], null, mapped[1]);
  return fallback || t('auth.login.error.failed', null, 'Login failed.');
}

export function translatedResponseMessage(t, response, successKey, successFallback, failureKey, failureFallback) {
  return response?.ok
    ? t(successKey, null, successFallback)
    : t(failureKey, null, failureFallback);
}
